import { useKeycloak } from "@react-keycloak/web";
import { useLogout } from "../LogoutProvider";
import { useServerDown } from "../ServerDownProvider";
import ApiError from "../Errors/ApiError";
import * as Sentry from "@sentry/react"; // <-- Make sure to import Sentry

// Ensure only one Keycloak token refresh runs at a time across all calls
let refreshInFlightPromise = null;

const useApiWrapper = () => {
  const { keycloak } = useKeycloak();
  const { handleSessionExpired, handleUnauthorized } = useLogout();
  const { showServerDownDialog } = useServerDown();

  // ------------------------------------------------------------------
  // HELPER: Reusable Sentry Error Capturing
  // ------------------------------------------------------------------
  const captureSentryError = async (response, url, requestOptions = {}) => {
    const targetStatusCodes = [400, 403, 422, 500, 501, 503, 504];

    if (targetStatusCodes.includes(response.status)) {
      try {
        const clonedResponse = response.clone();
        const errorBodyText = await clonedResponse.text();
        const method = requestOptions.method || "GET";

        Sentry.withScope((scope) => {
          scope.setTransactionName(`${method} ${url}`);
          scope.setTag("api_error", "true");
          scope.setTag("status_code", response.status.toString());
          scope.setTag("api_path", url);

          scope.setContext("Request Details", {
            url: url,
            method: method,
            body: requestOptions.body ? String(requestOptions.body) : "No Data",
          });

          scope.setContext("Response Details", {
            body: errorBodyText || "Empty Body",
          });

          Sentry.captureException(new Error(`API Error ${response.status}: ${method} ${url}`));
        });
      } catch (sentryErr) {
        console.error("Failed to log API error to Sentry:", sentryErr);
      }
    }
  };
  // ------------------------------------------------------------------

  const fetchWithAuth = async (url, options = {}) => {
    try {
      if (keycloak.authenticated) {
        try {
          // Reuse an in-flight refresh; otherwise start a new one
          if (!refreshInFlightPromise) {
            refreshInFlightPromise = new Promise((resolve, reject) => {
              keycloak.updateToken(30)
                .then(result => {
                  // result can be true/false; both are valid
                  resolve(result);
                })
                .catch(err => {
                  // err may be “true”, “false”, or something non-Error
                  reject(
                    err instanceof Error
                      ? err
                      : new Error(`Keycloak updateToken rejected with non-error: ${String(err)}`)
                  );
                });
            });
          }
          await refreshInFlightPromise;
        } catch (tokenError) {
          console.error("Token refresh failed:", tokenError);
          handleSessionExpired();
          throw new ApiError(401, ""); // Throwing to exit the function with an error object with empty message as we already handled the session expiry
        } finally {
          // Clear the in-flight promise once done
          refreshInFlightPromise = null;
        }
      }
      const token = keycloak.token;
      const headers = {
        Authorization: `Bearer ${token}`,
        ...options.headers,
      };
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // ---> TRIGGER SENTRY CAPTURE HERE
      await captureSentryError(response, url, options);

      if (response.status === 401) {
        handleSessionExpired();
        throw new ApiError(401, ""); // Throwing to exit the function with an error object with empty message as we already handled the session expiry
      }

      if (response.status === 403) {
        handleUnauthorized();
        throw new ApiError(403, ""); // Throwing to exit the function with an error object with empty message as we already handled the unauthorized access
      }

      if (response.status === 404) {
        const errorData = await response.json();
        const errorMessage =
          errorData.errorMessage || errorData.detail || "No Data Found";
        throw new ApiError(response.status, errorMessage);
      }

      // Check for server errors (500, 502, 503, 504)
      if ([500, 502, 503, 504].includes(response.status)) {
        showServerDownDialog();
        throw new ApiError(response.status, ""); // Throwing to exit the function with an error object with empty message as we already handled the server down scenario
      }

      if (
        response.status === 304 ||
        response.status === 204 ||
        response.headers.get("Content-Length") === "0"
      ) {
        return null;
      }
      if (
        response.status === 200 &&
        response.headers.get("Content-Length") === "0"
      ) {
        return true;
      }
      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage =
          errorData.errorMessage || errorData.detail || "API request failed";
        throw new Error(errorMessage);
      }
      return await response.json();
    } catch (error) {
      console.error("API Error:", error.message);
      throw error;
    }
  };

  const downloadFileWithAuth = async (url, fileName) => {
    try {
      if (keycloak.authenticated) {
        try {
          if (!refreshInFlightPromise) {
            refreshInFlightPromise = keycloak.updateToken(30);
          }
          await refreshInFlightPromise;
        } catch (tokenError) {
          const normalizedError =
            tokenError instanceof Error
              ? tokenError
              : new Error(`Keycloak updateToken rejected: ${String(tokenError)}`);
          console.error("Token refresh failed:", normalizedError);
          handleSessionExpired();
          throw new ApiError(401, "");
        } finally {
          refreshInFlightPromise = null;
        }
      }
      const token = keycloak.token;

      // I am extracting the options into a variable so we can pass it to Sentry
      const requestOptions = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(url, requestOptions);

      // ---> TRIGGER SENTRY CAPTURE HERE
      await captureSentryError(response, url, requestOptions);

      if (response.status === 401) {
        handleSessionExpired();
        throw new ApiError(401, "");
      }

      if (response.status === 403) {
        handleUnauthorized();
        throw new ApiError(403, "");
      }

      if (response.status === 404) {
        const errorData = await response.json();
        const errorMessage =
          errorData.errorMessage || errorData.detail || "No Data Found";
        throw new ApiError(response.status, errorMessage);
      }

      if ([500, 502, 503, 504].includes(response.status)) {
        showServerDownDialog();
        throw new ApiError(response.status, "");
      }

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true };
    } catch (error) {
      console.error("Download Error:", error.message);
      throw error;
    }
  };

  return { fetchWithAuth, downloadFileWithAuth };
};

export default useApiWrapper;